import React, { useState } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Plus,
  MoreHorizontal,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Edit,
  UserPlus,
  Trash2,
} from "lucide-react"
import { formatDateOnly, formatTimestamp } from "@/lib/datetime"
import { Link } from "react-router-dom"
import { ActionItemResponse } from "@/lib/types"

interface PatientActionItemsTabProps {
  patientId: string
  actionItems?: ActionItemResponse[]
  loading?: boolean
  onCreateItem?: () => void
  onCompleteItem?: (itemId: string) => void
  onEditItem?: (item: ActionItemResponse) => void
  onReassignItem?: (itemId: string) => void
  onDeleteItem?: (itemId: string) => void
  onReopenItem?: (itemId: string) => void
}

export function PatientActionItemsTab({
  patientId,
  actionItems = [],
  loading = false,
  onCreateItem,
  onCompleteItem,
  onEditItem,
  onReassignItem,
  onDeleteItem,
  onReopenItem,
}: PatientActionItemsTabProps) {
  const [showCompleted, setShowCompleted] = useState(false)

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-40" />
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
      </div>
    )
  }

  const openItems = actionItems.filter(
    (item) => item.status !== "completed" && item.status !== "cancelled"
  )
  const completedItems = actionItems.filter(
    (item) => item.status === "completed" || item.status === "cancelled"
  )

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case "URGENT":
        return (
          <Badge className="bg-destructive/10 text-destructive hover:bg-destructive/10">
            Urgent
          </Badge>
        )
      case "MEDIUM":
        return (
          <Badge className="bg-warning/10 text-warning hover:bg-warning/10">
            Medium
          </Badge>
        )
      case "LOW":
        return (
          <Badge variant="outline" className="text-muted-foreground">
            Low
          </Badge>
        )
      default:
        return null
    }
  }

  const getAssigneeBadge = (assignee: string | null | undefined) => {
    if (!assignee) return null
    switch (assignee) {
      case "PEAR_AI":
        return (
          <Badge variant="outline" className="border-info/30 text-info">
            Pear AI
          </Badge>
        )
      case "CARE_TEAM":
        return (
          <Badge variant="outline" className="border-success/30 text-success">
            Care Team
          </Badge>
        )
      case "ADMIN":
        return (
          <Badge variant="outline" className="border-primary/30 text-primary">
            Admin
          </Badge>
        )
      default:
        return <Badge variant="outline">{assignee}</Badge>
    }
  }

  const renderActionItem = (item: ActionItemResponse, isCompleted = false) => (
    <Card key={item.id} className={isCompleted ? "opacity-60" : ""}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <Checkbox
            checked={isCompleted}
            onCheckedChange={() =>
              isCompleted
                ? onReopenItem?.(item.id)
                : onCompleteItem?.(item.id)
            }
            className="mt-1"
          />

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <h4
                  className={`font-medium ${
                    isCompleted ? "line-through text-muted-foreground" : ""
                  }`}
                >
                  <Link to={`/action-items/${item.id}`} className="hover:underline">
                    {item.title}
                  </Link>
                </h4>
                {item.description && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {item.description}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {getPriorityBadge(item.priority)}
              </div>
            </div>

            <div className="flex items-center gap-3 mt-2 text-sm text-muted-foreground">
              {item.assigned_users && item.assigned_users.length > 0 && (
                <span>
                  Assigned to:{" "}
                  {item.assigned_users.map((u: any) => u.name || u.email).join(", ")}
                </span>
              )}
              {item.assignee && getAssigneeBadge(item.assignee)}
              {item.due_date && (
                <>
                  <span>•</span>
                  <span>Due: {formatDateOnly(item.due_date, "MMM d")}</span>
                </>
              )}
            </div>

            <div className="text-xs text-muted-foreground mt-2">
              Created {formatTimestamp(item.created_at, "MMM d, yyyy")}
            </div>
          </div>

          {!isCompleted && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onCompleteItem?.(item.id)}>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Complete
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onEditItem?.(item)}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onReassignItem?.(item.id)}>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Reassign
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onDeleteItem?.(item.id)}
                  className="text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </CardContent>
    </Card>
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">
          Open Action Items ({openItems.length})
        </h2>
        <Button onClick={onCreateItem} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Create Item
        </Button>
      </div>

      {/* Open Items */}
      {openItems.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle className="h-12 w-12 mx-auto mb-4 text-success" />
            <p className="text-muted-foreground mb-4">No open action items</p>
            <Button variant="outline" onClick={onCreateItem}>
              Create Action Item
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {openItems.map((item) => renderActionItem(item))}
        </div>
      )}

      {/* Completed Items */}
      {completedItems.length > 0 && (
        <Collapsible open={showCompleted} onOpenChange={setShowCompleted}>
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              className="flex items-center gap-2 w-full justify-start p-0 h-auto hover:bg-transparent"
            >
              {showCompleted ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              <span className="text-muted-foreground">
                Completed Items ({completedItems.length})
              </span>
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-3 mt-3">
            {completedItems.map((item) => renderActionItem(item, true))}
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  )
}

export default PatientActionItemsTab
